import { Box, SpaceBetween, Header, Alert, AppLayout, ContentLayout, ProgressBar, Button, RadioGroup } from '@amzn/awsui-components-core';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { AvatarSize } from '@amzn/stencil-react-components/avatar';
import { fetchEmployeesBatchForOrgChart, TimeoutError, withTimeout } from 'src/api/baseball-card-service';
import { getEmployeeByLogin, countAllReportsUnderLeader, fetchAllReportsRecursively, fetchDirectReports } from 'src/api/papi-service';
import { HomePageSideNavigationV2 } from 'src/components/side-navigation-v2/HomePageSideNavigationV2';
import { useAuth } from '../../context/AuthContextProvider';

import { logger } from 'src/logger';
import { buildBreadcrumbChain } from 'src/utilities/RouteConfig';
import BaseballCardEmployeeUserAvatar from './employee-avatars/BaseballCardEmployeeUserAvatar';
import { OrgChartTree } from './components/org-chart/OrgChartTree';
import { EmployeeSearchBar } from '../ea-huddle-v2/components/attendees/EmployeeSearchBar';
import { ParticipantSelector } from '../ea-huddle-v2/components/attendees/ParticipantSelector';
import { Employee } from '../ea-huddle-v2/interfaces/interface';
import { generateBulkPdfsAsZipWithFolders, downloadBlob } from './utils/pdfGenerator';
import { OrgChartEmployee } from './types/orgChart.types';
import { convertEmployeeToOrgChartEmployee, convertLoginsToEmails } from './utils/employeeTypeAdapters';
import { transformToOrgChartEmployee } from './utils/employeeTransformers';
import BASEBALL_CARD_PDF from './document/2025_Baseball_Card.pdf';
import { MAX_EMPLOYEES, API_TIMEOUT_MS } from './constants/BaseballCardConstants';
import { DisclaimerContainer } from './components/DisclaimerContainer';
import { getS3Client, parseS3URI } from 'src/services/aws/S3Service';
import { GetObjectCommand } from '@aws-sdk/client-s3';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';

/**
 * Helper function to check if an employee has a valid job level.
 * Returns false if jobLevel is null, undefined, or empty string.
 */
const hasValidJobLevel = (employee: OrgChartEmployee): boolean => {
  return employee.jobLevel !== null && employee.jobLevel !== undefined && employee.jobLevel.trim() !== '';
};

export const BaseballCardsHomePage: React.FC = () => {
  const { Alias } = useAuth();
  const [currentLeaderAlias, setCurrentLeaderAlias] = useState<string>(Alias);
  const [currentLeaderData, setCurrentLeaderData] = useState<OrgChartEmployee | null>(null);
  const [employees, setEmployees] = useState<Map<string, OrgChartEmployee>>(new Map());

  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [manuallyAddedEmployees, setManuallyAddedEmployees] = useState<Map<string, OrgChartEmployee>>(new Map());
  const [removedEmployees, setRemovedEmployees] = useState<Set<string>>(new Set());

  const [viewMode, setViewMode] = useState<'direct' | 'all'>('direct');

  const [navigationOpen, setNavigationOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [pdfTotal, setPdfTotal] = useState(0);

  useEffect(() => {
    logger.info('User authenticated successfully!', { app: 'Baseball Cards' });
  }, []);

  const activeEmployeeCount = useMemo(() => {
    let count = 0;
    // Count all employees with valid jobLevel (including leader)
    employees.forEach((employee) => {
      if (hasValidJobLevel(employee)) {
        count++;
      }
    });
    return count;
  }, [employees]);

  const existingLogins = useMemo(() => {
    return new Set([...Array.from(employees.keys()), ...Array.from(manuallyAddedEmployees.keys())]);
  }, [employees, manuallyAddedEmployees]);

  const existingEmails = useMemo(() => {
    return convertLoginsToEmails(existingLogins, employees);
  }, [existingLogins, employees]);

  // Filter employees for display - only include those with valid jobLevel
  const filteredEmployeesForDisplay = useMemo(() => {
    const filtered = new Map<string, OrgChartEmployee>();
    employees.forEach((employee, login) => {
      if (login === currentLeaderAlias || hasValidJobLevel(employee) || employee.isManuallyAdded) {
        filtered.set(login, employee);
      }
    });
    return filtered;
  }, [employees, currentLeaderAlias]);

  const handleNavigationChange = useCallback(({ detail }: { detail: { open: boolean } }) => {
    setNavigationOpen(detail.open);
  }, []);

  const handleExpand = useCallback(
    async (login: string) => {
      setExpandedNodes((prev) => {
        const newSet = new Set(prev);
        newSet.add(login);
        return newSet;
      });

      const employee = employees.get(login);

      if (employee && employee.directReports.length > 0) {
        const missingReports = employee.directReports.filter(
          (reportLogin) => !employees.has(reportLogin) && reportLogin !== currentLeaderAlias && !removedEmployees.has(reportLogin)
        );

        if (missingReports.length > 0) {
          try {
            const fetchedEmployees = await fetchEmployeesBatchForOrgChart(missingReports);

            setEmployees((prev) => {
              const newMap = new Map(prev);
              fetchedEmployees.forEach((emp, empLogin) => {
                if (!newMap.has(empLogin)) {
                  newMap.set(empLogin, emp);
                }
              });
              return newMap;
            });
          } catch (error) {
            logger.error('Failed to fetch direct reports', {
              login,
              missingReports,
              error: error instanceof Error ? error.message : String(error)
            });
          }
        }
      }
    },
    [employees, currentLeaderAlias, removedEmployees]
  );

  const handleCollapse = useCallback((login: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      newSet.delete(login);
      return newSet;
    });
  }, []);

  /**
   * Recursively get all employee logins under a given manager.
   * Uses the directReports array to traverse the org hierarchy.
   */
  const getEmployeesUnderManager = useCallback((managerLogin: string, employeesMap: Map<string, OrgChartEmployee>): Set<string> => {
    const result = new Set<string>();
    const manager = employeesMap.get(managerLogin);

    if (manager && manager.directReports.length > 0) {
      for (const reportLogin of manager.directReports) {
        if (employeesMap.has(reportLogin)) {
          result.add(reportLogin);
          // Recursively get reports under this report
          const nestedReports = getEmployeesUnderManager(reportLogin, employeesMap);
          nestedReports.forEach((login) => result.add(login));
        }
      }
    }

    return result;
  }, []);

  const handleRemove = useCallback(
    (login: string) => {
      // Get all employees under the removed person (including nested reports)
      const employeesToRemove = getEmployeesUnderManager(login, employees);
      employeesToRemove.add(login); // Include the person being removed

      setEmployees((prev) => {
        const newMap = new Map(prev);
        employeesToRemove.forEach((empLogin) => newMap.delete(empLogin));
        return newMap;
      });

      setManuallyAddedEmployees((prev) => {
        const newMap = new Map(prev);
        employeesToRemove.forEach((empLogin) => newMap.delete(empLogin));
        return newMap;
      });

      setExpandedNodes((prev) => {
        const newSet = new Set(prev);
        employeesToRemove.forEach((empLogin) => newSet.delete(empLogin));
        return newSet;
      });

      setRemovedEmployees((prev) => {
        const newSet = new Set(prev);
        employeesToRemove.forEach((empLogin) => newSet.add(empLogin));
        return newSet;
      });
    },
    [employees, getEmployeesUnderManager]
  );

  const handleAddEmployee = useCallback((eaHuddleEmployee: Employee, _priority: 'required' | 'optional') => {
    const employee = convertEmployeeToOrgChartEmployee(eaHuddleEmployee);
    const employeeWithFlag = { ...employee, isManuallyAdded: true };

    setEmployees((prev) => {
      const newMap = new Map(prev);
      newMap.set(employee.login, employeeWithFlag);
      return newMap;
    });

    setManuallyAddedEmployees((prev) => {
      const newMap = new Map(prev);
      newMap.set(employee.login, employeeWithFlag);
      return newMap;
    });

    setRemovedEmployees((prev) => {
      const newSet = new Set(prev);
      newSet.delete(employee.login);
      return newSet;
    });
  }, []);

  const handleViewModeChange = useCallback(({ detail }: { detail: { value: string } }) => {
    const newMode = detail.value as 'direct' | 'all';
    setViewMode(newMode);
  }, []);

  const handleLeaderChange = useCallback(async (eaHuddleEmployee: Employee) => {
    const newLeaderAlias = eaHuddleEmployee.login_name?.toLowerCase() || '';

    if (!newLeaderAlias) {
      setErrorMessage('Invalid employee selected. Please try again.');
      return;
    }

    setCurrentLeaderAlias(newLeaderAlias);
    setCurrentLeaderData(null);
    setEmployees(new Map());
    setExpandedNodes(new Set());
    setRemovedEmployees(new Set());
    // Note: manuallyAddedEmployees is intentionally NOT cleared here
    // so that manually added employees persist across leader changes
    setLoadingProgress(0);
    setTotalEmployees(0);
    setErrorMessage(null);
    setIsLoading(true);
  }, []);

  /**
   * Fetches the PDF template from S3 and returns it as a blob URL.
   * Returns null if the fetch fails.
   */
  const fetchPdfFromS3 = useCallback(async (): Promise<string | null> => {
    try {
      const s3Client = getS3Client();
      if (!s3Client) {
        logger.warn('Failed to initialize S3 client for PDF template fetch');
        return null;
      }

      const stage = ENVIRONMENT_VARIABLES.env.Stage === 'dev' ? 'alpha' : ENVIRONMENT_VARIABLES.env.Stage;
      const s3URI = `s3://baseball-cards-${stage}/2025_Baseball_Card.pdf`;
      const { bucket, s3Key } = parseS3URI(s3URI);

      const getCommand = new GetObjectCommand({
        Bucket: bucket,
        Key: s3Key
      });

      const response = await s3Client.send(getCommand);

      if (response.Body) {
        const bodyBytes = await response.Body.transformToByteArray();
        // Create a new ArrayBuffer from the Uint8Array to ensure proper type compatibility
        const arrayBuffer = new ArrayBuffer(bodyBytes.length);
        new Uint8Array(arrayBuffer).set(bodyBytes);
        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);

        logger.info('Using PDF template from S3', { s3URI });
        return blobUrl;
      }

      logger.warn('Empty response body from S3 for PDF template', { s3URI });
      return null;
    } catch (error) {
      logger.warn('Failed to fetch PDF template from S3, will use local fallback', {
        error: error instanceof Error ? error.message : String(error)
      });
      return null;
    }
  }, []);

  const handleGeneratePdfs = useCallback(async () => {
    let s3BlobUrl: string | null = null;

    try {
      setIsGeneratingPdf(true);
      setErrorMessage(null);
      setPdfProgress(0);

      const activeEmployees: OrgChartEmployee[] = [];
      employees.forEach((employee) => {
        if (hasValidJobLevel(employee)) {
          activeEmployees.push(employee);
        }
      });

      setPdfTotal(activeEmployees.length);

      logger.info('Starting PDF generation', { totalEmployees: activeEmployees.length });

      // Try to fetch PDF template from S3 first, fallback to local
      s3BlobUrl = await fetchPdfFromS3();
      let pdfTemplatePath: string;

      if (s3BlobUrl) {
        pdfTemplatePath = s3BlobUrl;
        logger.info('Using PDF template from S3');
      } else {
        pdfTemplatePath = BASEBALL_CARD_PDF;
        logger.info('Using local PDF template (S3 fetch failed or unavailable)');
      }

      const zipBlob = await generateBulkPdfsAsZipWithFolders(pdfTemplatePath, activeEmployees, currentLeaderAlias, (current, total) => {
        setPdfProgress(current);
        setPdfTotal(total);
      });

      const filename = `${currentLeaderAlias}_Reports_Baseball_Cards.zip`;
      downloadBlob(zipBlob, filename);

      logger.info('PDF generation completed');
    } catch (error) {
      logger.error('Failed to generate PDFs');
      setErrorMessage('Failed to generate PDFs. Please try again.');
    } finally {
      // Clean up S3 blob URL to free memory
      if (s3BlobUrl) {
        URL.revokeObjectURL(s3BlobUrl);
      }
      setIsGeneratingPdf(false);
      setPdfProgress(0);
      setPdfTotal(0);
    }
  }, [employees, currentLeaderAlias, fetchPdfFromS3]);

  useEffect(() => {
    // Create AbortController to cancel pending requests when viewMode or leader changes
    const abortController = new AbortController();
    const signal = abortController.signal;

    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        setLoadingProgress(0);
        setTotalEmployees(0);

        let allAliases: string[] = [];

        if (viewMode === 'direct') {
          // Fetch direct reports using PAPI
          const directReportAliases = await fetchDirectReports(currentLeaderAlias, signal);

          allAliases = directReportAliases.filter((alias) => alias !== currentLeaderAlias);
        } else {
          try {
            // First, count total reports to fail fast if exceeding MAX_EMPLOYEES
            const totalReportsCount = await withTimeout(
              countAllReportsUnderLeader(currentLeaderAlias, signal),
              API_TIMEOUT_MS,
              'API_TIMEOUT',
              signal
            );

            if (totalReportsCount > MAX_EMPLOYEES) {
              setErrorMessage(
                `Cannot load All Reports view for more than ${MAX_EMPLOYEES} employees. ` +
                  `Found ${totalReportsCount} employees. ` +
                  `Please use Direct Reports view or select a leader with fewer reports.`
              );
              setIsLoading(false);
              return;
            }

            // Now fetch all report aliases recursively since count is within limits
            const allReportAliases = await fetchAllReportsRecursively(currentLeaderAlias, signal);

            allAliases = allReportAliases.filter((alias) => alias !== currentLeaderAlias);
          } catch (error) {
            // Don't show error message if request was intentionally cancelled
            if (error instanceof Error && error.name === 'AbortError') {
              return;
            }

            if (error instanceof TimeoutError) {
              setErrorMessage(
                `Failed to load employees. This leader likely has a large number of reports and the request timed out. ` +
                  `Please use Direct Reports view instead.`
              );
            } else {
              setErrorMessage('Failed to load employee data. Please try again.');
            }
            setIsLoading(false);
            return;
          }
        }

        setTotalEmployees(allAliases.length + 1);

        // Fetch leader data
        const leaderResponse = await getEmployeeByLogin(currentLeaderAlias, 'job,direct-reports', signal);

        if (leaderResponse.employee) {
          const leaderEmployee = transformToOrgChartEmployee(leaderResponse.employee);
          setCurrentLeaderData(leaderEmployee);
        }

        // Fetch all employees
        const aliasesToFetch = [currentLeaderAlias, ...allAliases];
        const fetchedEmployees = await fetchEmployeesBatchForOrgChart(aliasesToFetch, setLoadingProgress, signal);

        // Merge fetched employees with manually added employees
        setEmployees(() => {
          const mergedEmployees = new Map(fetchedEmployees);

          // Re-add manually added employees that aren't in the fetched set
          manuallyAddedEmployees.forEach((employee, login) => {
            if (!mergedEmployees.has(login)) {
              mergedEmployees.set(login, employee);
            }
          });

          return mergedEmployees;
        });

        setExpandedNodes(new Set([currentLeaderAlias]));
        setIsLoading(false);

        logger.info('Initial data load completed', { totalEmployees: fetchedEmployees.size });
      } catch (error) {
        // Don't update state or show errors if request was intentionally cancelled
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        logger.error('Failed to load initial data');
        setErrorMessage('Failed to load employee data. Please try again.');
        setIsLoading(false);
      }
    };

    loadInitialData();

    // Cleanup function: abort pending requests when dependencies change
    return () => {
      abortController.abort();
    };
  }, [currentLeaderAlias, viewMode]);

  return (
    <>
      <AppLayout
        navigation={<HomePageSideNavigationV2 />}
        navigationOpen={navigationOpen}
        onNavigationChange={handleNavigationChange}
        contentType="default"
        toolsHide
        content={
          <ContentLayout
            header={
              <SpaceBetween size="m">
                <Header
                  variant="h1"
                  description={
                    <Box fontWeight="bold" fontSize="heading-m">
                      Employees ({activeEmployeeCount})
                    </Box>
                  }
                  actions={
                    <SpaceBetween direction="horizontal" size="m" alignItems="center">
                      {currentLeaderData && (
                        <BaseballCardEmployeeUserAvatar
                          userInfo={{
                            login: currentLeaderData.login,
                            firstName: currentLeaderData.firstName,
                            lastName: currentLeaderData.lastName,
                            jobLevel: currentLeaderData.jobLevel,
                            jobTitle: currentLeaderData.jobTitle,
                            costCenterName: currentLeaderData.department,
                            city: currentLeaderData.city,
                            country: currentLeaderData.country
                          }}
                          badgeColor={currentLeaderData.badgeColor}
                          avatarSize={AvatarSize.Small}
                          stack={false}
                          showAdditionalInfo={false}
                        />
                      )}

                      {/* <ParticipantSelector
                        onEmployeeSelect={handleLeaderChange}
                        placeholder="Search for new leader..."
                        existingEmails={currentLeaderData?.email ? new Set([currentLeaderData.email.toLowerCase()]) : new Set()}
                        buttonText="Select Leader"
                      /> */}

                      <RadioGroup
                        onChange={handleViewModeChange}
                        value={viewMode}
                        items={[
                          { value: 'direct', label: 'Direct Reports' },
                          { value: 'all', label: 'All Reports' }
                        ]}
                      />

                      <Button
                        variant="primary"
                        onClick={handleGeneratePdfs}
                        disabled={isLoading || isGeneratingPdf || activeEmployeeCount === 0}
                        loading={isGeneratingPdf}
                      >
                        {`Generate PDFs (${activeEmployeeCount})`}
                      </Button>
                    </SpaceBetween>
                  }
                >
                  Talent Review Card Builder
                </Header>
              </SpaceBetween>
            }
          >
            <SpaceBetween size="l">
              {errorMessage && (
                <Alert type="error" dismissible onDismiss={() => setErrorMessage(null)}>
                  {errorMessage}
                </Alert>
              )}

              {isLoading && (
                <SpaceBetween size="s">
                  <Box>Loading employee information...</Box>
                  <ProgressBar
                    value={totalEmployees > 0 ? (loadingProgress / totalEmployees) * 100 : 0}
                    additionalInfo={`${loadingProgress} / ${totalEmployees} employees loaded`}
                    label="Loading Progress"
                  />
                </SpaceBetween>
              )}

              {isGeneratingPdf && (
                <SpaceBetween size="s">
                  <Box>Generating PDF baseball cards...</Box>
                  <ProgressBar
                    value={pdfTotal > 0 ? (pdfProgress / pdfTotal) * 100 : 0}
                    additionalInfo={`${pdfProgress} / ${pdfTotal} PDFs generated`}
                    label="PDF Generation Progress"
                  />
                </SpaceBetween>
              )}

              {!isLoading && !errorMessage && (
                <SpaceBetween size="m">
                  {/* <EmployeeSearchBar
                    onAddAttendee={handleAddEmployee}
                    existingEmails={existingEmails}
                    placeholder="Search and add people..."
                    singleAddButton={true}
                    addButtonText="Add"
                    fullWidth={true}
                  /> */}

                  {currentLeaderData && (
                    <OrgChartTree
                      leader={currentLeaderData}
                      employees={filteredEmployeesForDisplay}
                      expandedNodes={expandedNodes}
                      onExpand={handleExpand}
                      onCollapse={handleCollapse}
                      onRemove={handleRemove}
                    />
                  )}

                  <DisclaimerContainer />
                </SpaceBetween>
              )}
            </SpaceBetween>
          </ContentLayout>
        }
      />
    </>
  );
};
